'use client'

import { useReportIssueModal } from '@/hooks/useReportIssueModal'
import { analytics } from '@/lib/analytics'
import { useAppSelector } from '@/lib/hooks'
import { useState } from 'react'
import { toast } from 'react-toastify'
import Modal from './Modal/Modal'

export function ReportIssueModal() {
  const { isOpen, setClose, scope } = useReportIssueModal()
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [feedback, setFeedback] = useState('')

  const { agent } = useAppSelector((state) => state.agent) || { agent: null }

  const handleReportIssueClick = async () => {
    analytics.reportIssue.reportIssue(String(scope), rating, agent || undefined, feedback)
    setClose()
    setRating(0)
    setFeedback('')

    toast.success('Done. Thanks for submitting your feedback!')
  }

  const handleStarClick = (starIndex: number) => {
    setRating(starIndex)
  }

  const handleStarHover = (starIndex: number) => {
    setHoverRating(starIndex)
  }

  const handleStarLeave = () => {
    setHoverRating(0)
  }

  return (
    <Modal isOpen={isOpen} onClose={setClose}>
      <div className="relative flex w-[600px] max-w-[95vw] flex-col items-center gap-8 rounded-3xl border border-white/10 bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d] p-8 shadow-2xl">
        <div className="relative flex w-full flex-col items-center gap-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="mb-3 text-3xl font-bold text-white">Report Issue</h2>
            <p className="text-lg text-white/60">
              Help us improve by sharing your experience and feedback.
            </p>
          </div>

          {/* Star Rating */}
          <div className="flex flex-col items-center gap-6">
            <p className="text-lg text-white/80">Rate your experience:</p>
            <div className="flex gap-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleStarClick(star)}
                  onMouseEnter={() => handleStarHover(star)}
                  onMouseLeave={handleStarLeave}
                  className="transform transition-all duration-200 hover:scale-125"
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 24 24"
                    fill={star <= (hoverRating || rating) ? '#8b5cf6' : 'none'}
                    stroke={star <= (hoverRating || rating) ? '#8b5cf6' : '#ffffff30'}
                    strokeWidth="2"
                    className="cursor-pointer drop-shadow-lg"
                  >
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Textarea */}
          <div className="w-full">
            <label htmlFor="feedback" className="mb-4 block text-lg text-white/80">
              Tell us more about the issue:
            </label>
            <div className="relative">
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Describe the issue you encountered..."
                className="h-40 w-full resize-none rounded-2xl border border-white/10 bg-[#2a2a2a] px-6 py-4 text-lg text-white placeholder-white/40 shadow-inner transition-all duration-300 focus:border-purple-500/50 focus:bg-[#2f2f2f] focus:outline-none"
                rows={6}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-2 flex w-full gap-4">
            <button
              onClick={setClose}
              className="flex-1 rounded-full border border-white/20 bg-white/10 px-6 py-4 font-medium text-white transition-all duration-200 hover:border-white/30 hover:bg-white/20"
            >
              Cancel
            </button>
            <button
              onClick={handleReportIssueClick}
              className="flex-1 rounded-full bg-gradient-to-r from-purple-600 to-violet-600 px-6 py-4 font-medium text-white shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-violet-700 hover:shadow-purple-500/25"
            >
              Submit Report
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
